import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Avatar,
  Tag,
  Alert,
  Popconfirm,
  message,
  Spin,
} from 'antd'
import {
  InstagramOutlined,
  FacebookOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  LinkOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useCustom, useInvalidate } from '@refinedev/core'
import { authFetch } from '@/utils/authFetch'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const { Title, Text } = Typography

type SocialPlatform = 'INSTAGRAM' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER' | 'YOUTUBE'
type SocialAccountStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'ERROR'

interface SocialAccount {
  id: string
  platform: SocialPlatform
  accountName: string
  accountHandle: string | null
  profileImageUrl: string | null
  status: SocialAccountStatus
  tokenExpiresAt: string | null
  lastSyncedAt: string | null
  errorMessage: string | null
  createdAt: string
}

const platformConfig: Record<
  SocialPlatform,
  { icon: React.ReactNode; color: string; name: string; bgColor: string }
> = {
  INSTAGRAM: {
    icon: <InstagramOutlined style={{ fontSize: 32 }} />,
    color: '#E4405F',
    name: 'Instagram',
    bgColor: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
  },
  FACEBOOK: {
    icon: <FacebookOutlined style={{ fontSize: 32 }} />,
    color: '#1877F2',
    name: 'Facebook',
    bgColor: '#1877F2',
  },
  TIKTOK: {
    icon: <span style={{ fontSize: 24, fontWeight: 'bold' }}>TT</span>,
    color: '#000000',
    name: 'TikTok',
    bgColor: '#000000',
  },
  TWITTER: {
    icon: <TwitterOutlined style={{ fontSize: 32 }} />,
    color: '#1DA1F2',
    name: 'X (Twitter)',
    bgColor: '#1DA1F2',
  },
  YOUTUBE: {
    icon: <YoutubeOutlined style={{ fontSize: 32 }} />,
    color: '#FF0000',
    name: 'YouTube',
    bgColor: '#FF0000',
  },
}

const statusConfig: Record<SocialAccountStatus, { color: string; text: string; icon: React.ReactNode }> = {
  ACTIVE: { color: 'success', text: 'Povezan', icon: <CheckCircleOutlined /> },
  EXPIRED: { color: 'warning', text: 'Token istekao', icon: <ClockCircleOutlined /> },
  REVOKED: { color: 'error', text: 'Pristup opozvan', icon: <ExclamationCircleOutlined /> },
  ERROR: { color: 'error', text: 'Greska', icon: <ExclamationCircleOutlined /> },
}

// All platforms we support
const allPlatforms: SocialPlatform[] = ['INSTAGRAM', 'FACEBOOK', 'TIKTOK', 'TWITTER', 'YOUTUBE']

export default function SocialMediaAccountsPage() {
  const navigate = useNavigate()
  const invalidate = useInvalidate()

  // Fetch connected accounts
  const { query: accountsQuery } = useCustom<SocialAccount[]>({
    url: '/admin/social-media/accounts',
    method: 'get',
  })

  const isLoading = accountsQuery.isLoading
  const rawData = accountsQuery.data?.data
  // Handle various API response shapes
  const accounts: SocialAccount[] = (() => {
    if (Array.isArray(rawData)) return rawData
    if (rawData && typeof rawData === 'object' && 'data' in rawData && Array.isArray((rawData as { data: unknown }).data)) {
      return (rawData as { data: SocialAccount[] }).data
    }
    return []
  })()

  // Connect account
  const handleConnect = async (platform: SocialPlatform) => {
    try {
      const redirectUri = `${window.location.origin}/social-media/accounts/callback`

      const response = await authFetch(
        `${API_URL}/admin/social-media/accounts/${platform}/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`
      )

      if (!response.ok) throw new Error('Failed to get auth URL')

      const { url } = await response.json()

      // Redirect to OAuth provider
      window.location.href = url
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    }
  }

  // Disconnect account
  const handleDisconnect = async (platform: SocialPlatform) => {
    try {
      const response = await authFetch(`${API_URL}/admin/social-media/accounts/${platform}`, {
        method: 'DELETE',
      })

      if (!response.ok) throw new Error('Failed to disconnect')

      message.success(`${platformConfig[platform].name} nalog je odspojen`)
      invalidate({ resource: 'social-accounts', invalidates: ['all'] })
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    }
  }

  // Refresh token
  const handleRefreshToken = async (platform: SocialPlatform) => {
    try {
      const response = await authFetch(`${API_URL}/admin/social-media/accounts/${platform}/refresh`, {
        method: 'POST',
      })

      if (!response.ok) throw new Error('Failed to refresh token')

      message.success('Token je osvezen')
      invalidate({ resource: 'social-accounts', invalidates: ['all'] })
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    }
  }

  // Render platform card
  const renderPlatformCard = (platform: SocialPlatform) => {
    const config = platformConfig[platform]
    const account = accounts.find((a: SocialAccount) => a.platform === platform)
    const isConnected = !!account

    return (
      <Col xs={24} sm={12} lg={6} key={platform}>
        <Card
          style={{
            height: '100%',
            borderTop: `4px solid ${config.color}`,
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} align="center">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: config.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              {config.icon}
            </div>
            <Title level={5} style={{ margin: 0 }}>
              {config.name}
            </Title>

            {isConnected && account ? (
              <>
                <Space>
                  {account.profileImageUrl && (
                    <Avatar src={account.profileImageUrl} size="small" />
                  )}
                  <Text strong>{account.accountName}</Text>
                </Space>
                {account.accountHandle && (
                  <Text type="secondary">@{account.accountHandle}</Text>
                )}
                <Tag
                  color={statusConfig[account.status].color}
                  icon={statusConfig[account.status].icon}
                >
                  {statusConfig[account.status].text}
                </Tag>

                {account.tokenExpiresAt && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Token istice:{' '}
                    {new Date(account.tokenExpiresAt).toLocaleDateString('sr-Latn-RS')}
                  </Text>
                )}

                {account.errorMessage && (
                  <Alert message={account.errorMessage} type="error" style={{ marginTop: 8 }} />
                )}

                <Space style={{ marginTop: 16 }}>
                  {account.status === 'EXPIRED' && (
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => handleRefreshToken(platform)}
                    >
                      Osvezi token
                    </Button>
                  )}
                  <Popconfirm
                    title="Da li ste sigurni?"
                    description="Ovo ce odspojiti nalog i obrisati sve tokene."
                    onConfirm={() => handleDisconnect(platform)}
                    okText="Da, odspoji"
                    cancelText="Odustani"
                  >
                    <Button size="small" danger icon={<DisconnectOutlined />}>
                      Odspoji
                    </Button>
                  </Popconfirm>
                </Space>
              </>
            ) : (
              <>
                <Text type="secondary">Nije povezan</Text>
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={() => handleConnect(platform)}
                  style={{ marginTop: 16, background: config.color, borderColor: config.color }}
                >
                  Povezi nalog
                </Button>
              </>
            )}
          </Space>
        </Card>
      </Col>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 24, width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Povezani nalozi
          </Title>
          <Text type="secondary">
            Povezi naloge na drustvenim mrezama za objavljivanje sadrzaja
          </Text>
        </div>
        <Button onClick={() => navigate('/social-media')}>Nazad na postove</Button>
      </Space>

      <Alert
        message="OAuth integracija u razvoju"
        description="Povezivanje naloga putem OAuth-a ce biti dostupno uskoro. Za sada mozete videti status povezanih naloga."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={[16, 16]}>{allPlatforms.map(renderPlatformCard)}</Row>
      )}

      <Card title="Napomene" style={{ marginTop: 24 }}>
        <Space direction="vertical">
          <Text>
            <strong>Instagram:</strong> Zahteva Instagram Business ili Creator nalog povezan sa
            Facebook stranicom.
          </Text>
          <Text>
            <strong>Facebook:</strong> Objavljuje na Facebook stranicu, ne na licni profil.
          </Text>
          <Text>
            <strong>TikTok:</strong> Zahteva TikTok Business nalog.
          </Text>
          <Text>
            <strong>X (Twitter):</strong> Koristi Twitter API v2 sa OAuth 2.0.
          </Text>
          <Text>
            <strong>YouTube:</strong> Zahteva Google nalog sa YouTube kanalom. Koristi YouTube Data API v3.
          </Text>
        </Space>
      </Card>
    </div>
  )
}
